import { useFieldArray, useForm } from "react-hook-form";
import EMRSectionSearch from "@/app/components/ui/Reusable/EMRSectionSearch";
import {
  EMRdetailsProps,
  IEMRSectionSearchResponseData,
  IEMRUpdatePayloadProps,
} from "@/api_framework/api_modals/EMRs";
import InputWithStartAndEndAddOn from "@/app/components/ui/Inputs/InputWithStartAndEndAddOn";
import Input from "@/app/components/ui/Inputs/Input";
import { debounce, toNumber } from "lodash";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { useEffect, useMemo, useRef, useState } from "react";
import { useUpdateTempEMRAPI } from "@/app/hooks/api_hooks/emr/useUpdateTempEMRAPI";
import { useEMRDetailsAPI } from "@/app/hooks/api_hooks/emr/useEMRDetailsAPI";
import { AnimatePresence, motion } from "motion/react";
import toast from "react-hot-toast";
import { IClickLogBody, useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";
import { LOCKED_CHILD_ITEM_IDS } from "@/constants";
import EMRItemsSuggestionsComponent from "../EMRItemsSuggestionsComponent";

type DewormingSectionFormFieldType = {
  treatment_id: string | null;
  name: string;
  remark: string;
  quantity: number;
  item_tag?: string;
  prescription_type?: string;
  deworming_product_type?: string;
  is_hd_stock?: boolean;
  is_child?: boolean;
  applied_item_group_id?: string;
};

export interface IEMRDewormingSectionFormFields {
  deworming_items: DewormingSectionFormFieldType[];
}

const isLockedChildDewormingItem = (item?: Partial<DewormingSectionFormFieldType>) =>
  Boolean(item?.is_child && item?.treatment_id && LOCKED_CHILD_ITEM_IDS.includes(item.treatment_id));

const getDewormingProductType = (
  item?: Partial<IEMRSectionSearchResponseData> & { deworming_product_type?: string },
): string => {
  const explicitType = item?.deworming_product_type;
  if (typeof explicitType === "string" && explicitType.trim() !== "") {
    return explicitType.trim();
  }
  return "medicine";
};

const normalizeDewormingItemsFromDetails = (details?: IEMRUpdatePayloadProps | null): DewormingSectionFormFieldType[] => {
  const fromDewormingPayload = details?.deworming_meds_treatments;
  const fallbackTreatmentItems = details?.treatments?.treatments_added;

  const rawItems = (Array.isArray(fromDewormingPayload)
    ? fromDewormingPayload
    : Array.isArray(fallbackTreatmentItems)
      ? fallbackTreatmentItems
      : []) as any[];

  return rawItems.map((item) => {
    const quantityValue = toNumber(item?.quantity);
    return {
      treatment_id: item?.id ?? item?.vetic_variant_id ?? item?.treatment_id ?? item?.item_id ?? null,
      name: item?.name ?? item?.variant_name ?? item?.item_name ?? "",
      remark: item?.remark ?? "",
      quantity: Number.isFinite(quantityValue) && quantityValue > 0 ? quantityValue : 1,
      item_tag: item?.item_tag ?? "#deworming",
      prescription_type: item?.prescription_type ?? undefined,
      deworming_product_type: getDewormingProductType(item),
      is_hd_stock: item?.is_hd_stock ?? undefined,
      is_child: item?.is_child ?? false,
      applied_item_group_id: item?.applied_item_group_id ?? undefined,
    };
  });
};

const mapDewormingItemsForUpdate = (items: DewormingSectionFormFieldType[] = []) => {
  return items.map((item) => {
    const quantityValue = toNumber(item?.quantity);
    return {
      id: item?.treatment_id ?? null,
      name: item?.name ?? "",
      quantity: Number.isFinite(quantityValue) && quantityValue > 0 ? quantityValue : 1,
      deworming_product_type: getDewormingProductType(item),
      remark: item?.remark ?? "",
      item_tag: item?.item_tag ?? "#deworming",
      prescription_type: item?.prescription_type ?? undefined,
      is_hd_stock: item?.is_hd_stock ?? undefined,
      is_child: item?.is_child ?? false,
      applied_item_group_id: item?.applied_item_group_id ?? undefined,
    };
  });
};

const DewormingSection = ({ emrDetails, setEmrDetails }: EMRdetailsProps) => {
  const { execute: updateEmrAPI } = useUpdateTempEMRAPI();
  const fetchEMRDetailsAPI = useEMRDetailsAPI();
  const [animateLast, setAnimateLast] = useState(false);
  const [invalidQuantities, setInvalidQuantities] = useState<Record<number, boolean>>({});

  const { execute: logAPI } = useClickLogAPI();

  const params = new URLSearchParams(location.search);
  const emr_id = params.get("emr_id");
  const emr_status = params.get("emr_status");

  const customerId = emrDetails?.customer_details?.customer_id;
  const pet_id = emrDetails?.pet_details?.pet_id;
  const userLogInfo = useAppSelector((state) => state.auth?.user?.email);

  const formHook = useForm<IEMRDewormingSectionFormFields>({
    mode: "onChange",
  });

  const { mappedClinicId, mappedClinicName } = useAppSelector((state) => state.root.UIStates);

  const dewormingFormHook = useFieldArray<IEMRDewormingSectionFormFields>({
    name: "deworming_items",
    control: formHook.control,
  });

  const isFirstLoad = useRef(true);

  // True while the user has local edits that are not yet persisted. Set by
  // scheduleAutoSave/watch, cleared when the debounced save actually runs or
  // when a direct API call sends the current form values anyway.
  const pendingSaveRef = useRef(false);

  const syncDewormingFormFromDetails = (details?: IEMRUpdatePayloadProps | null) => {
    // The user has unsaved local edits (e.g. a quantity change) and this sync
    // was triggered by data that predates them (another section's save, or a
    // response from before the edit). Persist the local edits NOW and keep the
    // form as-is — replacing it here would silently reset quantities.
    if (pendingSaveRef.current) {
      debouncedUpdate.flush();
      return;
    }

    const dewormingItems = normalizeDewormingItemsFromDetails(details);
    const normalizedRows = dewormingItems.map((item) => {
      if (isLockedChildDewormingItem(item) && toNumber(item?.quantity) <= 0) {
        return {
          ...item,
          quantity: 1,
        };
      }
      return item;
    });

    isFirstLoad.current = false;
    debouncedUpdate.cancel();
    formHook.reset({ deworming_items: normalizedRows });
    dewormingFormHook.replace(normalizedRows);
    setTimeout(() => {
      isFirstLoad.current = true;
    }, 0);
  };

  // Debounced API function

  const debouncedUpdate = useMemo(
    () =>
      debounce(async (data) => {
        pendingSaveRef.current = false;
        const currentItemsPayload = mapDewormingItemsForUpdate(data?.deworming_items || []);
        const existingItemsPayload = mapDewormingItemsForUpdate(normalizeDewormingItemsFromDetails(emrDetails));
        if (JSON.stringify(currentItemsPayload) === JSON.stringify(existingItemsPayload)) return;

        const payload: IEMRUpdatePayloadProps = {
          deworming_meds_treatments: currentItemsPayload,
          remove_children: emrDetails?.remove_children,
          emr_status: emrDetails?.emr_status ?? emr_status,
        };
        const response = await updateEmrAPI(emr_id, payload);
        if (response?.status) {
          syncDewormingFormFromDetails(response.message);
          setEmrDetails?.(response.message);
        }
      }, 1000),
    [emr_id, emrDetails?.deworming_meds_treatments, emrDetails?.treatments?.treatments_added, setEmrDetails],
  );

  // Quantity steppers/inputs mutate the form via setValue, which RHF reports
  // with type undefined — the watch filter below ignores those. Every
  // user-driven programmatic mutation must call this to persist.
  const scheduleAutoSave = () => {
    pendingSaveRef.current = true;
    debouncedUpdate(formHook.getValues());
  };

  useEffect(() => {
    // Filter on `type === "change"` so programmatic reset/setValue never fires
    // debouncedUpdate. Refs-based isFirstLoad gates were racing with RHF's
    // subscription-callback timing and letting spurious calls through.
    const subscription = formHook.watch((value, { type }) => {
      if (type !== "change") return;
      pendingSaveRef.current = true;
      debouncedUpdate(value as IEMRDewormingSectionFormFields);
    });

    return () => subscription.unsubscribe();
  }, [formHook, debouncedUpdate]);

  useEffect(() => {
    if (emrDetails) {
      syncDewormingFormFromDetails(emrDetails);
    }
  }, [emrDetails]);

  const isChilderRemovefromEMR = (index: number, item: any) => {
    // Payload below is built from getValues(), so any pending debounced edits
    // are already included — drop the pending save so the response sync runs.
    debouncedUpdate.cancel();
    pendingSaveRef.current = false;
    dewormingFormHook.remove(index);
    const existing = formHook.getValues("deworming_items");
    let remove_children = null;
    if (item?.is_child) {
      remove_children = [...(emrDetails?.remove_children ?? []), item?.treatment_id];
    } else {
      remove_children = [...(emrDetails?.remove_children ?? [])];
    }

    const payload: IEMRUpdatePayloadProps = {
      deworming_meds_treatments: mapDewormingItemsForUpdate(existing),
      remove_children: remove_children,
      emr_status: emrDetails?.emr_status ?? emr_status,
    };

    updateEmrAPI(emr_id, payload).then(({ status, message }) => {
      if (status) {
        syncDewormingFormFromDetails(message);
        setEmrDetails(message);
      }
    });
    callLogAPILabDeleted(item?.treatment_id);
  };

  const handleAddDeworming = async (item: IEMRSectionSearchResponseData, source?: string) => {
    const existing = formHook.getValues("deworming_items");
    const itemName = item?.name ?? item?.variant_name ?? item?.item_name ?? "";
    const isPresent = dewormingFormHook.fields.filter((f) => f.name === itemName);
    if (isPresent.length > 0) {
      toast("Deworming item already added.");
      return;
    }
    // Payload below includes pending edits via getValues(); cancel only after
    // the duplicate early-return so a rejected add keeps the pending save.
    debouncedUpdate.cancel();
    pendingSaveRef.current = false;

    const newItem = {
      name: itemName,
      remark: (item as any)?.remark ?? "",
      quantity: toNumber((item as any)?.quantity) || 1,
      treatment_id: item?.vetic_variant_id ?? item?.item_id ?? (item as any)?.id ?? null,
      item_tag: item?.item_tag ?? "#deworming",
      prescription_type: item?.prescription_type ?? undefined,
      deworming_product_type: getDewormingProductType(item),
      is_hd_stock: item?.is_hd_stock ?? undefined,
      is_child: item?.is_child ?? false,
      applied_item_group_id: (item as any)?.applied_item_group_id ?? undefined,
    };

    setAnimateLast(true);

    const addedList = [...existing, newItem];

    const payload: IEMRUpdatePayloadProps = {
      deworming_meds_treatments: mapDewormingItemsForUpdate(addedList),
      remove_children: emrDetails?.remove_children,
      emr_status: emrDetails?.emr_status ?? emr_status,
    };
    await updateEmrAPI(emr_id, payload).then(({ status, message }) => {
      if (status) {
        syncDewormingFormFromDetails(message);
        setEmrDetails(message);
      }
    });
    fetchEMRDetailsAPI(emr_id).then((freshResponse) => {
      if (freshResponse?.status) {
        syncDewormingFormFromDetails(freshResponse.data);
        setEmrDetails?.(freshResponse.data);
      }
    });
    source && callLogSuggestionAPI(item);
    // SUGGESTION_ITEMS
  };

  useEffect(() => {
    if (!animateLast) return;
    const t = setTimeout(() => setAnimateLast(false), 600);
    return () => clearTimeout(t);
  }, [animateLast, emrDetails]);

  const callLogAPIBase = (
    item: string,
    eventType: "EMR_QUANTITY_PLUS" | "EMR_QUANTITY_MINUS" | "EMR_ITEM_DELETED" | "SUGGESTION_ITEMS",
  ) => {
    const fieldValue = formHook.watch("deworming_items");

    const logBody: IClickLogBody = {
      event_type: eventType,
      scr_id: "EMR",
      client_id: customerId,
      emr_id: emr_id,
      pet_id: pet_id,
      click_on_search_item: item || undefined,
      treatments_section: fieldValue || undefined,
      clinic_id: mappedClinicId,
      emr_search_type: "Deworming",
      clinic_name: mappedClinicName,
      vms_user_id: userLogInfo,
      timestamp: Date.now(),
    };

    logAPI(logBody);
  };

  const callLogAPILab = (item: any) => callLogAPIBase(item, "EMR_QUANTITY_PLUS");
  const callLogSuggestionAPI = (item: any) => callLogAPIBase(item, "SUGGESTION_ITEMS");
  const callLogAPILabMinus = (item: any) => callLogAPIBase(item, "EMR_QUANTITY_MINUS");

  const callLogAPILabDeleted = (item: string) => callLogAPIBase(item, "EMR_ITEM_DELETED");

  return (
    <div className=" w-full py-3 bg-white p-3 rounded-[10px] mt-3">
      <div className="bg-[#F9FAFB] rounded-xl p-2  ">
        <div className="flex flex-col gap-2 pb-2 sm:flex-row sm:gap-10 sm:items-center">
          <h3 className="shrink-0 text-[#000000] text-sm font-medium tracking-tight42 sm:w-2/12">Deworming</h3>
          <div className="w-full sm:flex-1 sm:-mt-1">
            <EMRSectionSearch
              clinicId={mappedClinicId}
              emrType={emrDetails?.emr_type}
              onSelectItem={(item) => {
                handleAddDeworming(item);
              }}
              onFlexibleAdd={(itemName) => {
                if (itemName !== "") {
                  dewormingFormHook.append({
                    name: itemName,
                    remark: "",
                    quantity: 1,
                    treatment_id: null,
                    item_tag: "#deworming",
                    deworming_product_type: "medicine",
                  });
                  scheduleAutoSave();
                }
              }}
              placeholder="Search & Add Deworming"
              flexibleAdd={false}
              containerClassName="mt-2 w-full sm:max-w-[600px]"
              itemActiveClassName="bg-[#f6f6f6] text-black"
              inputContainerClassName="w-full"
              searchType="Deworming"
            />
          </div>
        </div>
        <div className="bg-white p-[10px] pb-[4px] rounded-lg ">
          {dewormingFormHook?.fields?.length > 0 && (
            <div className="rounded-xl overflow-x-auto">
              <table className="w-full min-w-[640px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                <thead className="bg-[#F5F5F5] text-[#1F1F23] rounded-md font-normal ">
                  <tr>
                  <th className="p-3 pl-2 w-1/2 font-medium text-left text-[10px] sm:text-xs">Item Names</th>
                  <th className="p-3 pl-2 w-1/4 font-medium text-center text-[10px] sm:text-xs">Quantity</th>
                  <th className="font-medium  w-1/4 text-center text-[10px] sm:text-xs ">Remarks</th>
                  <th className="font-medium w-1/4 text-center text-[10px] sm:text-xs pr-2">Actions</th>
                </tr>
                </thead>

                <tbody className="overflow-hidden  rounded-xl w-full">
                  <AnimatePresence>
                    {dewormingFormHook?.fields?.map((field, index) => {
                      const isFirst = index === 0;
                      const isLast = index === dewormingFormHook?.fields?.length - 1;
                      const isSingle = dewormingFormHook?.fields?.length === 1;
                      const isQuantityEditable = String(field?.deworming_product_type)
                        .trim()
                        .toLowerCase() == "medicine";
                      // stable key: prefer medicine_id (persisted id) or fallback to field.id
                      const rowKey = field.treatment_id ?? field.id ?? `${index}`;

                      // only the last row will use initial animation when animateLast is true
                      const initialProp = animateLast && isLast ? { opacity: 0, y: 10, scale: 0.97 } : undefined; // undefined = no initial mount animation

                      return (
                        <motion.tr
                          key={rowKey}
                          initial={initialProp}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 10, scale: 0.97 }}
                          // transition={{ duration: 0.28 }}
                          transition={{ duration: 0.45, ease: "easeInOut" }}
                          className={`
    bg-white overflow-hidden
    ${isSingle ? "rounded-bl-xl" : ""}
    ${isFirst && !isSingle ? "rounded-t-xl" : ""}
    ${isLast && !isSingle ? "rounded-bl-xl" : ""}
  `}
                        >
                          <td
                            className={`p-2  text-left ${isSingle
                              ? "rounded-tl-xl rounded-bl-xl"
                              : `${isFirst ? "rounded-tl-xl" : ""} ${isLast ? "rounded-b-xl" : ""}`.trim()
                              }`}
                          >
                            <input hidden {...formHook.register(`deworming_items.${index}.name`)} />
                            <span className="text-[10px] sm:text-xs break-words text-[#1F1F23] font-normal">
                              {formHook.getValues(`deworming_items.${index}.name`)}
                            </span>
                            {field?.is_child && (
                              <p className="items-center rounded-md  px-2 py-1 text-[10px] font-semibold w-[70px] ml-2  ring-1 ring-inset text-gray-500 bg-blue-50 ring-green-500/10 my-2">
                                Suggested
                              </p>
                            )}
                          </td>
                          <td className="p-2 text-center align-middle">
                            <div className="w-32 mx-auto">
                              {!isQuantityEditable ? (
                                <div className="h-[38px] px-3 flex items-center justify-center text-sm font-medium text-[#1F1F23]">
                                  {formHook.watch(`deworming_items.${index}.quantity`)}
                                </div>
                              ) : (
                                <InputWithStartAndEndAddOn
                                  startAddon="-"
                                  startAddOnCallback={() => {
                                    const currentQuantity = toNumber(
                                      formHook.watch(`deworming_items.${index}.quantity`),
                                    );
                                    if (currentQuantity !== 1) {
                                      formHook.setValue(
                                        `deworming_items.${index}.quantity`,
                                        currentQuantity - 1,
                                      );
                                    }
                                    scheduleAutoSave();
                                    callLogAPILabMinus(formHook.watch(`deworming_items.${index}.treatment_id`));
                                  }}
                                  step={1}
                                  endAddOnCallback={() => {
                                    formHook.setValue(
                                      `deworming_items.${index}.quantity`,
                                      formHook.watch(`deworming_items.${index}.quantity`) + 1,
                                    );
                                    scheduleAutoSave();
                                    callLogAPILab(formHook.watch(`deworming_items.${index}.treatment_id`));
                                  }}
                                  min={1}
                                  endAddon="+"
                                  data-emr-quantity="true"
                                  data-invalid-quantity={invalidQuantities[index] ? "true" : "false"}
                                  value={formHook.watch(`deworming_items.${index}.quantity`)}
                                  inputClassName={invalidQuantities[index] ? "!ring-red-500 !text-red-500" : ""}
                                  onChange={(e: any) => {
                                    const rawValue = e.target.value;
                                    if (rawValue === "") {
                                      formHook.setValue(`deworming_items.${index}.quantity`, "" as any);
                                      setInvalidQuantities((prev) => ({ ...prev, [index]: false }));
                                      return;
                                    }
                                    const parsedQuantity = toNumber(rawValue);
                                    if (!Number.isNaN(parsedQuantity)) {
                                      const safeQuantity = parsedQuantity;
                                      formHook.setValue(`deworming_items.${index}.quantity`, safeQuantity as any);
                                      setInvalidQuantities((prev) => ({
                                        ...prev,
                                        [index]: safeQuantity < 0 || !Number.isInteger(safeQuantity),
                                      }));
                                      if (safeQuantity > 0 && Number.isInteger(safeQuantity)) {
                                        scheduleAutoSave();
                                      }
                                    }
                                    callLogAPILab(formHook.watch(`deworming_items.${index}.treatment_id`));
                                  }}
                                  onBlur={() => {
                                    const currentValue: any = formHook.getValues(
                                      `deworming_items.${index}.quantity`,
                                    );
                                    const parsedQuantity = toNumber(currentValue);

                                    if (currentValue === "" || Number.isNaN(parsedQuantity)) {
                                      formHook.setValue(`deworming_items.${index}.quantity`, 1);
                                      setInvalidQuantities((prev) => ({ ...prev, [index]: false }));
                                      scheduleAutoSave();
                                      return;
                                    }

                                    if (parsedQuantity <= 0) {
                                      formHook.setValue(`deworming_items.${index}.quantity`, 1);
                                      setInvalidQuantities((prev) => ({ ...prev, [index]: false }));
                                      scheduleAutoSave();
                                      toast.error("Decimal, Zero and Negative values are not allowed !");
                                      return;
                                    }

                                    if (!Number.isInteger(parsedQuantity)) {
                                      setInvalidQuantities((prev) => ({ ...prev, [index]: true }));
                                      toast.error("Decimal, Zero and Negative values are not allowed !");
                                      return;
                                    }

                                    setInvalidQuantities((prev) => ({ ...prev, [index]: false }));
                                  }}
                                  type="number"
                                />
                              )}
                            </div>
                          </td>
                          <td className="p-2">
                            <div className="pt-2 pb-2 w-full">
                              <Input
                                errorMessages={[{ message: "Temperature is required.", type: "required" }]}
                                fieldError={formHook.formState.errors.deworming_items}
                                register={formHook.register(`deworming_items.${index}.remark`, {
                                  required: false,
                                })}
                                inputClassName="text-xs h-9"
                                type="text"
                                placeholder="Remarks"
                              />
                            </div>
                          </td>

                          <td
                            className={`p-2 text-center ${isSingle
                              ? "rounded-tr-xl rounded-br-xl"
                              : `${isFirst ? "rounded-tr-xl" : ""} ${isLast ? "rounded-br-xl" : ""}`.trim()
                              }`}
                          >
                            <button
                              className="w-8 h-8 flex items-center justify-center bg-[#F2F2F2] rounded-[10px]"
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                isChilderRemovefromEMR(index, field);
                              }}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="white"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className="size-5 text-[#ED0101]"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
                                />
                              </svg>
                            </button>
                          </td>
                        </motion.tr>
                      );
                    })}
                  </AnimatePresence>
                </tbody>
              </table>
            </div>
          )}

          <EMRItemsSuggestionsComponent
            handelCallback={(item, s) => handleAddDeworming(item, s)}
            suggestionData={emrDetails?.deworming_meds_treatments_suggestions}
          />
        </div>
      </div>
    </div>
  );
};
export default DewormingSection;
